import React, { useState } from 'react';
import { DollarSign, Users, Clock, Copy, Check, LogOut } from 'lucide-react';
import AffiliateClickFunnel from './AffiliateClickFunnel';

const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_LABEL = { paid: 'Paid', held: 'Held', pending: 'Pending', approved: 'Pending', void: 'Void' };
const STATUS_STYLE = {
  paid: 'bg-green-100 text-green-700',
  held: 'bg-blue-100 text-blue-700',
  void: 'bg-gray-100 text-gray-500 line-through',
};

// Shared by the legacy read-only token portal (AffiliatePortal.jsx) and the
// logged-in dashboard (AffiliateDashboard.jsx) so the two surfaces render the
// exact same numbers from the exact same shape — no second copy to drift.
export default function AffiliateDashboardView({ data, editable = false, onSavePayout, onLogout }) {
  const t = data.totals;
  const [payoutMethod, setPayoutMethod] = useState(data.payout_method || 'paypal');
  const [payoutDetails, setPayoutDetails] = useState(data.payout_details?.handle || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const savePayout = async () => {
    setSaving(true); setSaved(false);
    try {
      await onSavePayout({ payout_method: payoutMethod, payout_details: payoutDetails });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome, {data.name}</h1>
            <p className="text-gray-600 mt-1">You earn {Math.round(data.commission_rate * 100)}% of each referred company's subscription for 12 months from their signup date, as long as they stay an active customer. Month 1's commission is held until their 2nd payment, then released: you don't lose it, it's just protected against refunds.</p>
          </div>
          {onLogout && (
            <button onClick={onLogout} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-sm font-semibold text-gray-900 mb-2">Your referral link</p>
          <div className="flex items-center gap-3">
            <input readOnly value={data.link} className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono" />
            <button
              onClick={() => { navigator.clipboard.writeText(data.link); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="px-5 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}{copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between"><p className="text-sm text-gray-600">Active referrals</p><Users className="w-5 h-5 text-green-500" /></div>
            <p className="text-2xl font-bold mt-1">{t.referrals_active}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between"><p className="text-sm text-gray-600">Pending</p><Clock className="w-5 h-5 text-yellow-500" /></div>
            <p className="text-2xl font-bold mt-1">{t.referrals_pending}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between"><p className="text-sm text-gray-600">Held</p><Clock className="w-5 h-5 text-blue-500" /></div>
            <p className="text-2xl font-bold mt-1">{money(t.held_cents)}</p>
            <p className="text-xs text-gray-400 mt-1">Releases with their next payment</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between"><p className="text-sm text-gray-600">Unpaid balance</p><DollarSign className="w-5 h-5 text-indigo-500" /></div>
            <p className="text-2xl font-bold mt-1">{money(t.unpaid_cents)}</p>
          </div>
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg p-5 text-white">
            <p className="text-sm text-indigo-100">Total paid out</p>
            <p className="text-2xl font-bold mt-1">{money(t.paid_cents)}</p>
          </div>
        </div>

        <AffiliateClickFunnel data={data.click_funnel} />

        {editable && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Payout details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <select value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="paypal">PayPal</option>
                <option value="venmo">Venmo</option>
                <option value="check">Check</option>
                <option value="other">Other</option>
              </select>
              <input placeholder="Handle / address" value={payoutDetails} onChange={(e) => setPayoutDetails(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button onClick={savePayout} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save payout details'}
              </button>
              {saved && <span className="text-sm text-green-600">Saved.</span>}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Commission history</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b"><th className="py-2">Date</th><th>Amount</th><th>Status</th></tr></thead>
            <tbody>
              {data.commissions.length === 0 && <tr><td colSpan="3" className="py-4 text-gray-400">No commissions yet. Share your link to get started.</td></tr>}
              {data.commissions.map((c, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-2">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="font-medium">{money(c.commission_cents)}</td>
                  <td><span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[c.status] || 'bg-yellow-100 text-yellow-700'}`}>{STATUS_LABEL[c.status] || c.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-center text-xs text-gray-400">Payouts are sent manually. Questions? Reach out to your SurFox AI contact.</p>
      </div>
    </div>
  );
}
